import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams, useParams } from "react-router-dom";
import { 
  User, Building2, ChevronRight, Briefcase, Search, Mail, Lock, 
  ShieldCheck, Landmark, CheckCircle2, TrendingUp, Zap 
} from "lucide-react";
import { getTheme, fonts } from "../theme";
import Logo from "../components/Logo";
import Navbar from "../components/Navbar";
import SiteFooter from "../components/SiteFooter";
import { ErrorBanner, PasswordInput } from "../components/UI";
import api from "../api";

export default function Login({ onAuth, onLogout, themeMode = "dark", setThemeMode }) {
  const theme = getTheme("login", themeMode);
  const navigate = useNavigate();
  const { roleParam } = useParams();
  const [searchParams] = useSearchParams();

  const parseRole = (r) => {
    if (!r) return "employee";
    const str = String(r).toLowerCase().trim();
    if (str === "super_admin" || str === "super-admin" || str === "superadmin") return "super_admin";
    if (str === "employer" || str === "company") return "employer";
    if (str === "job_seeker" || str === "jobseeker" || str === "job-seeker" || str === "apply_job") return "job_seeker";
    if (str === "recruiter" || str === "hr" || str === "looking_job") return "recruiter";
    return "employee";
  };

  const currentRole = parseRole(roleParam || searchParams.get("role"));
  const [activeTab, setActiveTab] = useState(currentRole);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setActiveTab(currentRole);
    setEmail("");
    setPassword("");

    // Anti-autofill: Chrome/Edge password managers inject saved passwords 50ms-500ms after load
    const t1 = setTimeout(() => { setEmail(""); setPassword(""); }, 100);
    const t2 = setTimeout(() => { setEmail(""); setPassword(""); }, 300);
    const t3 = setTimeout(() => { setEmail(""); setPassword(""); }, 600);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [currentRole]);

  const switchTab = (newTab) => {
    setActiveTab(newTab);
    setError("");
    setEmail("");
    setPassword("");
    const urlRole = newTab === "job_seeker" ? "job-seeker" : newTab;
    navigate(`/login/${urlRole}`, { replace: true });
  };

  const getRoleForBackend = () => {
    if (activeTab === "super_admin") return "super_admin";
    if (activeTab === "recruiter") return "recruiter";
    if (activeTab === "employer") return "employer";
    if (activeTab === "job_seeker") return "job_seeker";
    return "employee";
  };

  const getSubmitLabel = () => {
    if (busy) return "Signing in…";
    if (activeTab === "super_admin") return "Sign in as Super Admin";
    if (activeTab === "employee") return "Sign in as Employee";
    if (activeTab === "employer") return "Sign in as Employer";
    if (activeTab === "job_seeker") return "Sign in as Job Seeker";
    if (activeTab === "recruiter") return "Sign in as Recruiter";
    return "Sign in";
  };

  const getEmailConfig = () => {
    const placeholder = "you@gmail.com or you@company.com";
    if (activeTab === "super_admin") return { label: "Super Admin Email", placeholder };
    if (activeTab === "employer") return { label: "Employer Email", placeholder };
    if (activeTab === "job_seeker") return { label: "Job Seeker Email", placeholder };
    if (activeTab === "recruiter") return { label: "Recruiter Email", placeholder };
    return { label: "Employee Email", placeholder };
  };

  const getRegisterInfo = () => {
    if (activeTab === "employer") return { text: "Register Company Account", link: "/register/employer" };
    if (activeTab === "recruiter") return { text: "Register Recruiter Account", link: "/register/recruiter" };
    if (activeTab === "job_seeker") return { text: "Register Job Seeker Account", link: "/register/job-seeker" };
    return { text: "Register Employee Account", link: "/register/employee" };
  };

  const getDashboardPath = (targetRole) => {
    const r = targetRole || activeTab;
    if (r === "super_admin") return "/super-admin";
    if (r === "recruiter" || r === "hr") return "/recruiter";
    if (r === "employer" || r === "company") return "/employer";
    if (r === "job_seeker" || r === "jobseeker" || r === "job-seeker") return "/job-seeker";
    return "/employee";
  };

  const isValidEmail = (value) => {
    if (!value || typeof value !== "string") return false;
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const trimmedEmail = (email || "").trim().toLowerCase();
    const trimmedPassword = (password || "").trim();

    if (!trimmedEmail) {
      setError("Please enter your registered email address.");
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      setError("Please enter a valid email address (personal or company email is allowed).");
      return;
    }
    if (!trimmedPassword) {
      setError("Please enter your password.");
      return;
    }
    if (trimmedPassword.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }

    setBusy(true);
    const backendRole = getRoleForBackend();
    try {
      const data = await api.post("/auth/login", {
        email: trimmedEmail,
        password: trimmedPassword,
        role: backendRole,
      });

      if (!data || !data.token || !data.user) {
        setError("Login failed. Invalid response from server. Please try again.");
        return;
      }

      // Only allow access when email/password verified for the selected role tab
      const returnedRole = String(data.user.role || "").toLowerCase();
      const expectedRole = backendRole;
      const roleOk =
        returnedRole === expectedRole ||
        (expectedRole === "job_seeker" && (returnedRole === "employee" || returnedRole === "job_seeker"));

      if (!roleOk) {
        setError(`This account is not registered as ${activeTab.replace("_", " ")}. Please use the correct role tab.`);
        return;
      }

      onAuth({ token: data.token, user: { ...data.user, role: expectedRole === "job_seeker" ? "job_seeker" : data.user.role } });
      navigate(getDashboardPath(expectedRole));
    } catch (err) {
      const apiMsg =
        err?.response?.data?.error ||
        err?.data?.error ||
        err?.message ||
        "Invalid email or password. Please try again.";
      setError(apiMsg);
    } finally {
      setBusy(false);
    }
  };

  const regInfo = getRegisterInfo();
  const emailConfig = getEmailConfig();

  return (
    <div className="min-vh-100 bg-body d-flex flex-column" style={{ fontFamily: fonts.body }}>
      <Navbar theme={theme} onLogout={onLogout} themeMode={themeMode} setThemeMode={setThemeMode} />

      {/* Full-Screen Laptop Layout: Left Hero Showcase + Right Form Panel */}
      <div className="container-fluid flex-grow-1 px-0">
        <div className="row g-0 min-vh-100-minus-navbar">
          
          {/* Left Hero Panel (Visible on Laptops & Desktops) */}
          <div className="col-lg-6 col-xl-7 d-none d-lg-flex flex-column justify-content-between p-5 bg-body-tertiary border-end">
            <div>
              
              <div className="d-inline-flex align-items-center gap-2 px-3 py-1.5 rounded-pill bg-primary bg-opacity-10 text-primary border border-primary-subtle mb-4 small fw-bold">
                <Zap size={14} /> OFFICIAL TRUST BUREAU FOR INDIA
              </div>

              <h1 className="display-6 fw-bold text-body mb-3" style={{ fontFamily: fonts.display, letterSpacing: "-0.02em" }}>
                Building Workplace Trust. <br />
                <span className="text-primary">Enabling Infinite Opportunities.</span>
              </h1>

              <p className="lead fs-6 text-secondary mb-4" style={{ maxWidth: 540 }}>
                Workforce Information Bureau India Limited (WIBIL) is India's premier workplace trust evaluation network connecting four key pillars.
              </p>

              {/* 4 Pillars Grid */}
              <div className="row g-3 my-4">
                {[
                  { title: "Employees", desc: "Verified scores, EPFO history, and professional credibility.", icon: User, badge: "bg-success-subtle text-success" },
                  { title: "Employers", desc: "GST auto-verification, workforce sentiment & background checks.", icon: Building2, badge: "bg-primary-subtle text-primary" },
                  { title: "Recruiters", desc: "Direct candidate trust index search & verified talent profiles.", icon: Search, badge: "bg-info-subtle text-info" },
                  { title: "Financial Institutions", desc: "Employment stability metrics & pre-approved loan eligibility.", icon: Landmark, badge: "bg-warning-subtle text-warning" },
                ].map((item, idx) => {
                  const ItemIcon = item.icon;
                  return (
                    <div key={idx} className="col-6">
                      <div className="card h-100 border p-3 rounded-4 shadow-sm bg-body">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <span className={`p-2 rounded-3 ${item.badge}`}>
                            <ItemIcon size={18} />
                          </span>
                          <h3 className="h6 fw-bold mb-0 text-body">{item.title}</h3>
                        </div>
                        <p className="small text-secondary mb-0">{item.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>

            {/* Bottom Stats Banner */}
            <div className="pt-4 border-top border-secondary border-opacity-25 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2">
                <ShieldCheck size={20} className="text-primary" />
                <span className="small fw-semibold text-body">Verified EPFO & GST Integration</span>
              </div>
              <div className="d-flex align-items-center gap-3">
                <span className="small text-secondary"><CheckCircle2 size={14} className="text-success me-1" strokeWidth={3} /> 900 Max Score</span>
                <span className="small text-secondary"><TrendingUp size={14} className="text-primary me-1" /> Real-time Analytics</span>
              </div>
            </div>

          </div>

          {/* Right Form Panel (Full width on Mobile/Tablet, Centered half on Laptop) */}
          <div className="col-12 col-lg-6 col-xl-5 d-flex flex-column justify-content-center p-4 p-md-5 bg-body">
            <div className="w-100 mx-auto" style={{ maxWidth: 480 }}>
              
              {/* Header Logo */}
              <div className="text-center mb-4">
                <div className="d-inline-flex p-3 rounded-4 bg-body-tertiary border mb-3 shadow-sm">
                  <Logo color="#0d6efd" size={32} subtitle={true} />
                </div>
                
                <h2 className="h5 fw-bold text-body mb-1" style={{ fontFamily: fonts.display }}>
                  {activeTab === "super_admin" ? "Super Admin Sign In" : "Account Sign In"}
                </h2>
                <p className="small text-secondary mb-0">
                  {activeTab === "super_admin"
                    ? "Restricted console for ratings, APIs, and platform totals"
                    : "Select your role to access your WIBIL workspace"}
                </p>
              </div>

              {activeTab !== "super_admin" && (
              <div className="p-2 rounded-4 bg-body-tertiary border mb-3 shadow-sm">
                <div className="row g-1 text-center">
                  {[
                    { key: "employee", label: "Employee", icon: User },
                    { key: "employer", label: "Employer", icon: Building2 },
                    { key: "job_seeker", label: "Job Seeker", icon: Briefcase },
                    { key: "recruiter", label: "Recruiter", icon: Search },
                  ].map(b => {
                    const isActive = activeTab === b.key;
                    const Icon = b.icon;
                    return (
                      <div key={b.key} className="col-6 col-sm-3">
                        <button
                          type="button"
                          onClick={() => switchTab(b.key)}
                          className={`w-100 py-3 px-1.5 rounded-3 btn border-0 d-flex flex-column align-items-center justify-content-center gap-1.5 transition-all ${isActive ? "btn-primary shadow" : "btn-link text-secondary text-decoration-none"}`}
                          style={{ fontFamily: fonts.display, fontSize: 13, fontWeight: 700 }}
                        >
                          <Icon size={18} />
                          <span style={{ whiteSpace: "nowrap" }}>{b.label}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              )}

              {/* Dynamic Role Form Card */}
              <form onSubmit={submit} className="card shadow-sm border rounded-4 overflow-hidden" autoComplete="off">
                {/* Anti-autofill dummy inputs to intercept browser password managers */}
                <input type="text" name="prevent_autofill_email" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />
                <input type="password" name="prevent_autofill_pass" style={{ display: "none" }} tabIndex={-1} autoComplete="off" />

                <div className="card-body p-4 p-md-4">
                  <ErrorBanner message={error} theme={theme} />
                  
                  <div className="mb-3">
                    <label className="form-label small fw-bold text-uppercase text-secondary mb-1" style={{ letterSpacing: "0.05em" }}>
                      {emailConfig.label}
                    </label>
                    <div className="input-group">
                      <span className="input-group-text bg-body-tertiary">
                        <Mail size={16} />
                      </span>
                      <input 
                        type="email" 
                        name="wibil_login_user_email"
                        required 
                        className="form-control"
                        value={email} 
                        onChange={e => { setEmail(e.target.value); setError(""); }} 
                        placeholder={emailConfig.placeholder} 
                        autoComplete="off"
                        inputMode="email"
                        spellCheck={false}
                      />
                    </div>
                    <div className="form-text" style={{ fontSize: 12 }}>
                      Personal emails (Gmail, Yahoo, Outlook) and company emails are both allowed for every role.
                    </div>
                  </div>

                  <div className="mb-4">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="form-label small fw-bold text-uppercase text-secondary mb-0" style={{ letterSpacing: "0.05em" }}>
                        Security Password
                      </label>
                      <Link
                        to={`/forgot-password/${activeTab === "job_seeker" ? "job-seeker" : activeTab}`}
                        className="small fw-bold text-primary text-decoration-none"
                        style={{ visibility: activeTab === "super_admin" ? "hidden" : "visible" }}
                      >
                        Forgot Password?
                      </Link>
                    </div>
                    <div className="input-group" style={{ overflow: "visible" }}>
                      <span className="input-group-text bg-body-tertiary">
                        <Lock size={16} />
                      </span>
                      <PasswordInput 
                        name="wibil_login_user_password"
                        required 
                        theme={theme}
                        className="form-control"
                        value={password} 
                        onChange={e => { setPassword(e.target.value); setError(""); }} 
                        placeholder="••••••••" 
                        autoComplete="off"
                        minLength={6}
                      />
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    disabled={busy || !email.trim() || !password.trim()} 
                    className="btn btn-primary w-100 py-3 fw-bold text-uppercase d-flex align-items-center justify-content-center gap-2 shadow-sm"
                    style={{ fontFamily: fonts.display, letterSpacing: "0.03em" }}
                  >
                    <ShieldCheck size={16} /> {getSubmitLabel()} <ChevronRight size={16} />
                  </button>
                </div>
              </form>

              {activeTab !== "super_admin" && (
              <div className="text-center mt-4">
                <span className="small text-secondary me-2">New to WIBIL?</span>
                <Link to={regInfo.link} className="small fw-bold text-primary text-decoration-underline">
                  {regInfo.text}
                </Link>
              </div>
              )}

            </div>
          </div>

        </div>
      </div>
      <SiteFooter />
    </div>
  );
}
